import { Check, X } from "lucide-react"
import { cn } from "@/lib/cn"
import { wordDiff, type DiffSegment } from "@/lib/word-diff"
import type {
  EscalationPolicy,
  PolicyAgentOption,
  PolicyUserOption,
  AgentPoolOption,
  PolicyReasonOption,
  PolicyTopicOption,
  AssigneeStrategy,
  PolicyPriority,
} from "@/types/escalation_policy"
import type { CustomFieldType } from "@/types/custom_field"

// Stream B mints the canonical `CustomFieldCatalog` type (fed via the
// presenter's Inertia prop) and exports the read-only `<PredicateSummary>`.
// Until that lands, we ship a structural shape here so the diff card type-
// checks. When B's branch merges, this local type gets replaced by an
// `import type { CustomFieldCatalog } from "@/lib/predicate-ast"`.
export type CustomFieldCatalogEntry = {
  key: string
  label: string
  fieldType: CustomFieldType | "system_state"
  choices?: { value: string; label: string }[]
}
export type CustomFieldCatalog = {
  appuser:     CustomFieldCatalogEntry[]
  agent_convo: CustomFieldCatalogEntry[]
}

// Diff card for an EscalationPolicy proposal — same accept/reject +
// field-only diff style as the skills ProposalDiffCard. We resolve FK
// ids (assignee_user_id, assignee_agent_pool_id, reroute_agent_id) into
// human-readable names via the option lookups the Index page already
// passes around.

export type PolicyProposal = {
  id: string
  kind: string
  input: Record<string, unknown>
  accepted: boolean
  number?: number
}

type Lookups = {
  policies:      EscalationPolicy[]
  users:         PolicyUserOption[]
  agents:        PolicyAgentOption[]
  agentPools:    AgentPoolOption[]
  reasonOptions: PolicyReasonOption[]
  topicOptions:  PolicyTopicOption[]
  customFieldCatalog: CustomFieldCatalog
}

type Props = {
  proposal: PolicyProposal
  onToggle: (id: string) => void
  onRemove: (id: string) => void
  lookups: Lookups
}

const KIND_LABEL: Record<string, string> = {
  add_policy:       "Add policy",
  update_policy:    "Update policy",
  archive_policy:   "Archive policy",
  reorder_policies: "Reorder policies",
}

// Fields the operator should never see in a diff.
const HIDDEN_FIELDS = new Set(["id", "position"])

// Long-form text fields use a unified word-diff (none today, but kept
// symmetric with the skills card so future fields like `description`
// land on prose-diff for free).
const PROSE_FIELDS = new Set<string>([])

// Reason/topic key arrays render as +/- chip lists.
const ARRAY_FIELDS = new Set([
  "match_reason_keys_include",  "match_reason_keys_exclude",
  "match_topic_keys_include",   "match_topic_keys_exclude",
])

// Friendly label per snake_case field key.
const FIELD_LABEL: Record<string, string> = {
  name: "Name",
  active: "Active",
  match_custom_fields:          "Match · custom fields",
  match_reason_keys_include:    "Reasons · include",
  match_reason_keys_exclude:    "Reasons · exclude",
  match_topic_keys_include:     "Topics · include",
  match_topic_keys_exclude:     "Topics · exclude",
  match_min_recent_escalations: "Min recent escalations",
  match_recent_window_days:     "Recent window (days)",
  prefer_topic_affinity:        "Prefer topic affinity",
  assignee_strategy:            "Assignee strategy",
  assignee_user_id:             "Assignee · user",
  assignee_agent_pool_id:       "Assignee · pool",
  reroute_agent_id:             "Reroute · AI agent",
  priority_override:            "Priority override",
  sla_first_response_minutes:   "SLA · first response (min)",
  sla_idle_response_minutes:    "SLA · idle response (min)",
  sla_resolution_minutes:       "SLA · resolution (min)",
}

const STRATEGY_LABEL: Record<AssigneeStrategy, string> = {
  unassigned:            "Leave unassigned",
  specific_user:         "Assign user",
  random_from_pool:      "Random from pool",
  round_robin_from_pool: "Round robin from pool",
  route_to_agent:        "Reroute to AI agent",
  send_to_queue:         "Send to queue",
}

const PRIORITY_LABEL: Record<PolicyPriority, string> = {
  urgent: "Urgent",
  high:   "High",
  normal: "Normal",
  low:    "Low",
}

export function PolicyProposalDiffCard({ proposal, onToggle, onRemove, lookups }: Props) {
  const summary = (proposal.input.summary as string | undefined) ?? ""
  const label = KIND_LABEL[proposal.kind] ?? proposal.kind

  // Header anchor — for update/archive show the target policy name.
  const targetName = useTargetPolicyName(proposal, lookups)

  return (
    <div
      className={cn(
        "rounded-control border px-3 py-2 text-xs transition",
        proposal.accepted
          ? "border-vip-gold/40 bg-vip-goldMute"
          : "border-border bg-white/[0.03]",
      )}
    >
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onToggle(proposal.id)}
            className={cn(
              "flex h-5 w-5 shrink-0 items-center justify-center rounded border transition",
              proposal.accepted
                ? "border-vip-gold/40 bg-vip-gold/20 text-vip-goldSoft"
                : "border-border-strong text-app-subtle hover:bg-white/5",
            )}
            aria-label={proposal.accepted ? "Reject proposal" : "Accept proposal"}
          >
            {proposal.accepted && <Check size={12} strokeWidth={3} />}
          </button>
          {proposal.number !== undefined && proposal.number !== null && (
            <span className="rounded-chip border border-border bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-app-subtle">
              #{proposal.number}
            </span>
          )}
          <span className="font-medium text-app-text">{label}</span>
          {targetName && (
            <code className="rounded bg-white/5 px-1 text-[10px] text-app-muted">{targetName}</code>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(proposal.id)}
          className="text-app-subtle hover:text-app-text"
          aria-label="Discard proposal"
        >
          <X size={12} strokeWidth={2} />
        </button>
      </header>

      {summary && <p className="mb-1.5 text-app-muted">{summary}</p>}

      <Body proposal={proposal} lookups={lookups} />
    </div>
  )
}

function useTargetPolicyName(p: PolicyProposal, lookups: Lookups): string | null {
  if (p.kind === "update_policy" || p.kind === "archive_policy") {
    const id = Number(p.input.id)
    const found = lookups.policies.find((x) => x.id === id)
    return found?.name ?? `#${id}`
  }
  if (p.kind === "add_policy") {
    return (p.input.name as string | undefined) ?? null
  }
  return null
}

function Body({ proposal, lookups }: { proposal: PolicyProposal; lookups: Lookups }) {
  switch (proposal.kind) {
    case "add_policy":     return <AddPolicyDiff input={proposal.input} lookups={lookups} />
    case "update_policy":  return <UpdatePolicyDiff input={proposal.input} lookups={lookups} />
    case "archive_policy": return <ArchivePolicyDiff input={proposal.input} lookups={lookups} />
    case "reorder_policies": return <ReorderDiff input={proposal.input} lookups={lookups} />
    default:
      return <p className="text-app-muted">Unknown proposal kind.</p>
  }
}

// New-policy diff. Renders every meaningful field as a green creation row
// (no before-state, brand-new rule). Hides id/position.
function AddPolicyDiff({ input, lookups }: { input: Record<string, unknown>; lookups: Lookups }) {
  const keys = Object.keys(input)
    .filter((k) => !HIDDEN_FIELDS.has(k) && k !== "summary")
    .filter((k) => valueIsMeaningful(input[k]))
  if (keys.length === 0) return <p className="text-[11px] text-app-subtle">No fields specified.</p>

  return (
    <div className="space-y-2">
      {keys.map((k) => (
        <FieldRow key={k} field={k} before={undefined} after={input[k]} lookups={lookups} />
      ))}
    </div>
  )
}

// Update-policy diff. Loads the persisted policy by id, renders only the
// fields that actually changed.
function UpdatePolicyDiff({ input, lookups }: { input: Record<string, unknown>; lookups: Lookups }) {
  const id = Number(input.id)
  const before = lookups.policies.find((p) => p.id === id) ?? null
  if (!before) {
    return <p className="text-[11px] text-app-subtle">Target policy #{id} not found in current set.</p>
  }
  const beforeRecord = before as unknown as Record<string, unknown>

  const candidateKeys = Object.keys(input)
    .filter((k) => k !== "id" && k !== "summary" && !HIDDEN_FIELDS.has(k))
  const changedKeys = candidateKeys.filter((k) => !shallowEqual(beforeRecord[k], input[k]))

  if (changedKeys.length === 0) {
    return <p className="text-[11px] text-app-subtle">No field changes.</p>
  }

  return (
    <div className="space-y-2">
      {changedKeys.map((k) => (
        <FieldRow key={k} field={k} before={beforeRecord[k]} after={input[k]} lookups={lookups} />
      ))}
    </div>
  )
}

function ArchivePolicyDiff({ input, lookups }: { input: Record<string, unknown>; lookups: Lookups }) {
  const id = Number(input.id)
  const found = lookups.policies.find((p) => p.id === id)
  return (
    <p className="text-app-muted">
      Archive {found ? <code className="rounded bg-white/5 px-1 text-app-text">{found.name}</code> : `policy #${id}`} (sets <code className="rounded bg-white/5 px-1">active=false</code>).
    </p>
  )
}

function ReorderDiff({ input, lookups }: { input: Record<string, unknown>; lookups: Lookups }) {
  const ids = Array.isArray(input.ids) ? (input.ids as Array<unknown>).map((x) => Number(x)) : []
  const before = [...lookups.policies].sort((a, b) => a.position - b.position).map((p) => p.id)
  const labelFor = (id: number) => lookups.policies.find((p) => p.id === id)?.name ?? `#${id}`
  return (
    <div className="text-[11px] space-y-1">
      <Row label="before" tone="muted">
        {before.length === 0 ? "—" : before.map((id, i) => (
          <span key={`b-${id}`}>
            {i > 0 && <span className="mx-1 text-app-subtle">›</span>}
            <code className="rounded bg-white/5 px-1 text-app-text">{labelFor(id)}</code>
          </span>
        ))}
      </Row>
      <Row label="after" tone="gold">
        {ids.length === 0 ? "—" : ids.map((id, i) => (
          <span key={`a-${id}`}>
            {i > 0 && <span className="mx-1 text-vip-goldSoft">›</span>}
            <code className="rounded bg-vip-goldMute px-1 text-vip-goldSoft">{labelFor(id)}</code>
          </span>
        ))}
      </Row>
    </div>
  )
}

function Row({ label, tone, children }: { label: string; tone: "muted" | "gold"; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className={cn("w-12 shrink-0 text-[10px] uppercase tracking-wide", tone === "gold" ? "text-vip-goldSoft" : "text-app-subtle")}>
        {label}
      </span>
      <span>{children}</span>
    </div>
  )
}

// Single-field row — picks the renderer by field name. `before === undefined`
// means a creation row (add_policy or a field newly set on update).
function FieldRow({
  field, before, after, lookups,
}: {
  field: string
  before: unknown
  after: unknown
  lookups: Lookups
}) {
  const labelText = FIELD_LABEL[field] ?? field

  let body: React.ReactNode
  if (field === "match_custom_fields") {
    body = <PredicateDiffRow before={before} after={after} catalog={lookups.customFieldCatalog} />
  } else if (PROSE_FIELDS.has(field)) {
    body = <ProseDiff before={typeof before === "string" ? before : ""} after={typeof after === "string" ? after : ""} />
  } else if (ARRAY_FIELDS.has(field)) {
    const b = Array.isArray(before) ? (before as string[]) : []
    const a = Array.isArray(after)  ? (after  as string[]) : []
    body = <ArrayDiff before={b} after={a} />
  } else {
    body = <ValueDiff before={fmtValue(field, before, lookups)} after={fmtValue(field, after, lookups)} />
  }

  return (
    <div>
      <div className="mb-0.5 text-[10px] uppercase tracking-wide text-app-subtle">{labelText}</div>
      {body}
    </div>
  )
}

// Predicate-tree diff. Operators never see raw JSON — we flatten both trees
// to canonical English leaf strings via the catalog, then render added /
// removed / unchanged. If the root combinator changed (ALL → ANY), we
// prepend a banner so the operator notices the AND/OR semantic flip.
type FlatLeaf = { english: string; rawKey: string }
type RootCombinator = "all" | "any" | "not" | "leaf" | "empty"

function PredicateDiffRow({
  before, after, catalog,
}: {
  before: unknown
  after: unknown
  catalog: CustomFieldCatalog
}) {
  const beforeLeaves = flattenPredicate(before, catalog)
  const afterLeaves  = flattenPredicate(after,  catalog)

  const beforeKeys = new Set(beforeLeaves.map((l) => l.rawKey))
  const afterKeys  = new Set(afterLeaves.map((l) => l.rawKey))

  const added     = afterLeaves.filter((l) => !beforeKeys.has(l.rawKey))
  const removed   = beforeLeaves.filter((l) => !afterKeys.has(l.rawKey))
  const unchanged = afterLeaves.filter((l) => beforeKeys.has(l.rawKey))

  const beforeRoot = rootCombinator(before)
  const afterRoot  = rootCombinator(after)
  const combinatorChanged =
    beforeRoot !== "empty" && afterRoot !== "empty" && beforeRoot !== afterRoot

  const isCreation = beforeRoot === "empty"
  const isClear    = afterRoot === "empty" && beforeRoot !== "empty"

  if (isClear) {
    return <p className="text-[11px] text-app-muted">Removes the custom-field constraint (rule will match regardless of custom fields).</p>
  }
  if (afterLeaves.length === 0 && beforeLeaves.length === 0) {
    return <p className="text-[11px] text-app-subtle">No constraint.</p>
  }

  return (
    <div className="text-[10px] space-y-0.5">
      {combinatorChanged && (
        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-vip-goldSoft">
          Combinator changed: {beforeRoot.toUpperCase()} → {afterRoot.toUpperCase()}
        </div>
      )}
      {isCreation && afterLeaves.length > 0 && (
        <div className="mb-1 text-[10px] uppercase tracking-wide text-app-subtle">
          New rule: {(afterRoot === "all" || afterRoot === "any") ? afterRoot.toUpperCase() : "single condition"}
        </div>
      )}
      {added.length > 0 && (
        <div className="space-y-0.5">
          {added.map((l, i) => (
            <div key={`a-${i}-${l.rawKey}`}>
              <span className="text-status-success">+ </span>
              <span className="text-status-success">{l.english}</span>
            </div>
          ))}
        </div>
      )}
      {removed.length > 0 && (
        <div className="space-y-0.5">
          {removed.map((l, i) => (
            <div key={`r-${i}-${l.rawKey}`}>
              <span className="text-status-error">− </span>
              <span className="text-status-error line-through decoration-status-error/60">{l.english}</span>
            </div>
          ))}
        </div>
      )}
      {!isCreation && unchanged.length > 0 && (
        <div className="space-y-0.5">
          {unchanged.map((l, i) => (
            <div key={`u-${i}-${l.rawKey}`} className="text-app-subtle">
              <span>· </span>
              <span>{l.english}</span>
            </div>
          ))}
        </div>
      )}
      {added.length === 0 && removed.length === 0 && !combinatorChanged && (
        <p className="text-[11px] text-app-subtle">No change.</p>
      )}
    </div>
  )
}

function rootCombinator(node: unknown): RootCombinator {
  if (node === null || node === undefined) return "empty"
  if (typeof node !== "object") return "empty"
  const obj = node as Record<string, unknown>
  if (Object.keys(obj).length === 0) return "empty"
  if (Array.isArray(obj.all)) return "all"
  if (Array.isArray(obj.any)) return "any"
  if (obj.not && typeof obj.not === "object") return "not"
  return "leaf"
}

// Walks a predicate tree and emits one English string per leaf. Composites
// (all / any / not) are unwrapped — the root combinator banner above is
// where ALL-vs-ANY shows up. Unknown keys / archived fields fall back to
// the raw dotted path with a "[unknown field]" marker.
function flattenPredicate(node: unknown, catalog: CustomFieldCatalog): FlatLeaf[] {
  if (node === null || node === undefined) return []
  if (typeof node !== "object") return []
  const obj = node as Record<string, unknown>
  const keys = Object.keys(obj)
  if (keys.length === 0) return []

  if (Array.isArray(obj.all)) {
    return (obj.all as unknown[]).flatMap((c) => flattenPredicate(c, catalog))
  }
  if (Array.isArray(obj.any)) {
    return (obj.any as unknown[]).flatMap((c) => flattenPredicate(c, catalog))
  }
  if (obj.not && typeof obj.not === "object") {
    return flattenPredicate(obj.not, catalog).map((l) => ({
      english: `NOT (${l.english})`,
      rawKey:  `not:${l.rawKey}`,
    }))
  }

  const field = keys[0]
  const expression = obj[field]
  if (!expression || typeof expression !== "object" || Array.isArray(expression)) return []
  const exprObj = expression as Record<string, unknown>
  const op = Object.keys(exprObj)[0]
  if (!op) return []
  const value = exprObj[op]

  const english = renderLeafEnglish(op, field, value, catalog)
  const rawKey  = canonicalLeafKey(op, field, value)
  return [{ english, rawKey }]
}

function renderLeafEnglish(
  op: string,
  field: string,
  value: unknown,
  catalog: CustomFieldCatalog,
): string {
  const def = lookupFieldDef(field, catalog)
  const fieldLabel = def?.label ?? field

  switch (op) {
    case "present": return `${fieldLabel} is set`
    case "blank":   return `${fieldLabel} is not set`
    case "eq":      return `${fieldLabel} = ${formatValue(value, def)}`
    case "neq":     return `${fieldLabel} ≠ ${formatValue(value, def)}`
    case "gte":     return `${fieldLabel} ≥ ${formatValue(value, def)}`
    case "lte":     return `${fieldLabel} ≤ ${formatValue(value, def)}`
    case "gt":      return `${fieldLabel} > ${formatValue(value, def)}`
    case "lt":      return `${fieldLabel} < ${formatValue(value, def)}`
    case "contains": {
      const vs = Array.isArray(value) ? value : value === null || value === undefined ? [] : [value]
      return `${fieldLabel} contains [${vs.map((v) => formatValue(v, def)).join(", ")}]`
    }
    case "matches": {
      return `${fieldLabel} matches /${String(value ?? "")}/`
    }
    default: return `${fieldLabel} ${op} ${formatValue(value, def)}`
  }
}

function canonicalLeafKey(op: string, field: string, value: unknown): string {
  const valuePart =
    op === "contains" ? JSON.stringify(Array.isArray(value) ? value : value === undefined ? [] : [value]) :
    op === "matches"  ? String(value ?? "") :
    op === "present" || op === "blank" ? "" :
    JSON.stringify(value ?? null)
  return `${op}|${field}|${valuePart}`
}

function lookupFieldDef(dottedField: string, catalog: CustomFieldCatalog): CustomFieldCatalogEntry | null {
  const parts = dottedField.split(".")
  if (parts.length < 2) return null
  const scope = parts[0] === "convo" ? "agent_convo" : parts[0] === "appuser" ? "appuser" : null
  if (!scope) return null
  const isCustomField = parts[1] === "custom_fields" && parts.length === 3
  const key = isCustomField ? parts[2] : parts.slice(1).join(".")
  const list = scope === "appuser" ? catalog.appuser : catalog.agent_convo
  return list.find((d) => d.key === key) ?? null
}

function formatValue(v: unknown, def: CustomFieldCatalogEntry | null): string {
  if (v === null || v === undefined || v === "") return "—"
  if (typeof v === "boolean") return v ? "true" : "false"
  if (typeof v === "number")  return String(v)
  if (typeof v === "string") {
    if (def?.choices) {
      const c = def.choices.find((x) => x.value === v)
      if (c) return JSON.stringify(c.label)
    }
    return JSON.stringify(v)
  }
  try { return JSON.stringify(v) } catch { return String(v) }
}

// Translate a raw column value into something operator-readable.
function fmtValue(field: string, value: unknown, lookups: Lookups): string {
  if (value === null || value === undefined || value === "") return "—"
  if (field === "assignee_strategy") {
    return STRATEGY_LABEL[value as AssigneeStrategy] ?? String(value)
  }
  if (field === "priority_override") {
    return PRIORITY_LABEL[value as PolicyPriority] ?? String(value)
  }
  if (field === "assignee_user_id") {
    const u = lookups.users.find((x) => x.id === Number(value))
    return u ? `${u.name} (${u.email})` : `#${value}`
  }
  if (field === "assignee_agent_pool_id") {
    const p = lookups.agentPools.find((x) => x.id === Number(value))
    return p ? p.name : `#${value}`
  }
  if (field === "reroute_agent_id") {
    const a = lookups.agents.find((x) => x.id === Number(value))
    return a ? a.name : `#${value}`
  }
  if (typeof value === "boolean") return value ? "yes" : "no"
  if (typeof value === "string" || typeof value === "number") return String(value)
  try { return JSON.stringify(value) } catch { return String(value) }
}

function valueIsMeaningful(v: unknown): boolean {
  if (v === null || v === undefined || v === "") return false
  if (Array.isArray(v) && v.length === 0) return false
  return true
}

// Single value before/after — small inline pill pair. Used for booleans,
// enums, and FK-resolved strings.
function ValueDiff({ before, after }: { before: string; after: string }) {
  if (before === after) {
    return <span className="text-[11px] text-app-subtle">no change</span>
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]">
      <code className="rounded border border-border bg-app-bg px-1.5 py-0.5 text-app-muted line-through decoration-app-subtle/60">
        {before}
      </code>
      <span className="text-app-subtle">→</span>
      <code className="rounded border border-vip-gold/30 bg-vip-goldMute px-1.5 py-0.5 text-vip-goldSoft">
        {after}
      </code>
    </div>
  )
}

function ArrayDiff({ before, after }: { before: string[]; after: string[] }) {
  const added   = after.filter((x) => !before.includes(x))
  const removed = before.filter((x) => !after.includes(x))
  if (added.length === 0 && removed.length === 0) {
    return <p className="text-[11px] text-app-subtle">No change.</p>
  }
  return (
    <div className="text-[10px] space-y-0.5">
      {added.length > 0 && (
        <div>
          <span className="text-status-success">+</span>
          {added.map((x) => (
            <code key={`a-${x}`} className="ml-1 rounded bg-status-success/10 px-1 text-status-success">{x}</code>
          ))}
        </div>
      )}
      {removed.length > 0 && (
        <div>
          <span className="text-status-error">−</span>
          {removed.map((x) => (
            <code key={`r-${x}`} className="ml-1 rounded bg-status-error/10 px-1 text-status-error line-through">{x}</code>
          ))}
        </div>
      )}
    </div>
  )
}

// Word-level prose diff (mirrors skills card). Reserved for future text
// fields on EscalationPolicy.
function ProseDiff({ before, after }: { before: string; after: string }) {
  const segments: DiffSegment[] = wordDiff(before, after)
  return (
    <div className="text-[11px]">
      <div className="max-h-64 overflow-auto whitespace-pre-wrap rounded border border-border bg-app-bg p-2 leading-relaxed text-app-text">
        {segments.length === 0 ? (
          <span className="text-app-muted">(no change)</span>
        ) : (
          segments.map((s, i) => <DiffChunk key={i} seg={s} />)
        )}
      </div>
    </div>
  )
}

function DiffChunk({ seg }: { seg: DiffSegment }) {
  if (seg.type === "equal") return <span className="text-app-muted">{seg.text}</span>
  if (seg.type === "add") {
    return <span className="rounded bg-status-success/15 text-status-success">{seg.text}</span>
  }
  return (
    <span className="rounded bg-status-error/15 text-status-error line-through decoration-status-error/60">
      {seg.text}
    </span>
  )
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  try { return JSON.stringify(a) === JSON.stringify(b) } catch { return false }
}
